import React, { useEffect, useRef } from 'react';
import { ArrowRight, CheckCircle, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
export function WeightLoss() {
  const containerRef = useRef(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleMouseMove = e => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      container.style.setProperty('--mouse-x', x);
      container.style.setProperty('--mouse-y', y);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  return <div ref={containerRef} className="min-h-screen w-full" style={{
    backgroundImage: 'radial-gradient(circle at calc(var(--mouse-x, 0.5) * 100%) calc(var(--mouse-y, 0.5) * 100%), rgba(187, 221, 255, 0.35), transparent 50%)',
    backgroundSize: '120% 120%',
    backgroundPosition: 'center'
  }} data-id="element-1087">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden" data-id="element-1088">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden" data-id="element-1089">
          <div className="absolute -top-[30%] -right-[10%] w-[70%] h-[70%] rounded-full bg-gradient-to-br from-[#BBDDFF]/20 to-transparent blur-3xl" data-id="element-1090"></div>
          <div className="absolute -bottom-[30%] -left-[10%] w-[70%] h-[70%] rounded-full bg-gradient-to-tr from-[#88AAFF]/10 to-transparent blur-3xl" data-id="element-1091"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-1092">
          <div className="max-w-4xl mx-auto" data-id="element-1093">
            <div className="inline-block mb-6 px-4 py-1 rounded-full bg-gradient-to-r from-[#BBDDFF]/20 to-[#C85A15]/20 backdrop-blur-sm border border-white/10" data-id="element-1094">
              <span className="text-sm font-medium bg-gradient-to-r from-[#BBDDFF] to-[#C85A15] bg-clip-text text-transparent" data-id="element-1095">
                Weight Loss Treatment
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1096">
              <span className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] bg-clip-text text-transparent" data-id="element-1097">
                Clinical Weight Loss
              </span>
              <br data-id="element-1098" />
              <span className="bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent" data-id="element-1099">
                That Actually Works
              </span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-10" data-id="element-1100">
              Medically supervised weight loss programs with GLP-1 medications
              and continuous support from dedicated healthcare providers.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1101">
              <a href="https://app.joinomu.com/patient/onboarding" target="_blank" rel="noopener noreferrer" className="flex items-center bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] text-white font-medium py-3 px-8 rounded-full hover:shadow-lg transition-all group" data-id="element-1102">
                Pricing
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} data-id="element-1103" />
              </a>
              <Link to="/mens-health" className="w-full sm:w-auto bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 font-medium py-3 px-8 rounded-full hover:shadow-lg transition-all text-center" data-id="element-1104">
                Explore Other Treatments
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* Treatment Details */}
      <section className="py-20 relative overflow-hidden" data-id="element-1105">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-[#BBDDFF]/10 blur-3xl" data-id="element-1106"></div>
        <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-[#88AAFF]/10 blur-3xl" data-id="element-1107"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-1108">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1109">
            <div data-id="element-1110">
              <div className="relative" data-id="element-1111">
                <img src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Weight Loss Treatment - Diverse group of women supporting each other" className="w-full h-auto object-cover rounded-xl mb-8" data-id="element-1112" />
                <h3 className="text-3xl font-bold mb-6 bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] bg-clip-text text-transparent" data-id="element-1113">
                  Personalized GLP-1 Medication Plans
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1114">
                  Our medical providers can prescribe custom compounded GLP-1
                  medications and provide access to FDA-approved GLP-1
                  medications like Wegovy, Saxenda, and Ozempic.
                </p>
                <ul className="space-y-3 mb-8" data-id="element-1115">
                  <li className="flex items-start" data-id="element-1116">
                    <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1117" />
                    <span data-id="element-1118">Prescription medications delivered to your door</span>
                  </li>
                  <li className="flex items-start" data-id="element-1119">
                    <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1120" />
                    <span data-id="element-1121">Dosage adjustments based on your progress</span>
                  </li>
                  <li className="flex items-start" data-id="element-1122">
                    <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1123" />
                    <span data-id="element-1124">
                      Medical monitoring to ensure safety and efficacy
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1125">
                    <Check className="text-green-500 mr-2 flex-shrink-0 mt-1" size={20} data-id="element-1126" />
                    <span data-id="element-1127">
                      Prescription strength weight loss medication packs
                      available to those not eligible for GLP-1 medications.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div data-id="element-1128">
              <h2 className="text-3xl font-bold mb-6" data-id="element-1129">
                <span className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] bg-clip-text text-transparent" data-id="element-1130">
                  A comprehensive approach to weight loss
                </span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1131">
                JoinOmu's weight loss program goes beyond medication. Our
                holistic approach combines medical expertise, nutritional
                guidance, and continuous support to help you achieve sustainable
                results.
              </p>
              <div className="space-y-6 mb-8" data-id="element-1132">
                <div className="bg-white dark:bg-gray-800/50 rounded-xl p-6 shadow-md border border-gray-100 dark:border-gray-700/30" data-id="element-1133">
                  <h3 className="text-xl font-semibold mb-3 flex items-center" data-id="element-1134">
                    <CheckCircle className="text-[#BBDDFF] mr-3 flex-shrink-0" size={24} data-id="element-1135" />
                    Dedicated Medical Provider
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1136">
                    Work with a healthcare provider who specializes in weight
                    management who's recognized by their peers for exellence in
                    their field.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800/50 rounded-xl p-6 shadow-md border border-gray-100 dark:border-gray-700/30" data-id="element-1137">
                  <h3 className="text-xl font-semibold mb-3 flex items-center" data-id="element-1138">
                    <CheckCircle className="text-[#BBDDFF] mr-3 flex-shrink-0" size={24} data-id="element-1139" />
                    Personalized Medications
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1140">
                    Receive presonalized GLP-1 treatments or customizable
                    prescription strength medication packs delivered to your
                    door based on your goals and eligibility.
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800/50 rounded-xl p-6 shadow-md border border-gray-100 dark:border-gray-700/30" data-id="element-1141">
                  <h3 className="text-xl font-semibold mb-3 flex items-center" data-id="element-1142">
                    <CheckCircle className="text-[#BBDDFF] mr-3 flex-shrink-0" size={24} data-id="element-1143" />
                    Continuous Monitoring & Support
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300" data-id="element-1144">
                    Track your progress through our platform including
                    medication tracking and wearable health metrics. Receive
                    ongoing support with unlimited messaging and regular
                    check-ins.
                  </p>
                </div>
              </div>
              <a href="https://app.joinomu.com/patient/onboarding" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] text-white font-medium py-3 px-10 rounded-full hover:shadow-lg transition-all text-lg" data-id="element-1145">
                Verify eligibility
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* Success Stories */}
      <section className="hidden py-24 relative overflow-hidden" data-id="element-1146">
        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-[#BBDDFF]/10 blur-3xl" data-id="element-1147"></div>
        <div className="absolute -bottom-[20%] -left-[10%] w-[70%] h-[70%] rounded-full bg-[#C85A15]/5 blur-3xl" data-id="element-1148"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-1149">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1150">
            <div className="inline-block mb-4 px-4 py-1 rounded-full bg-gradient-to-r from-[#BBDDFF]/10 to-[#C85A15]/10 backdrop-blur-sm border border-white/10" data-id="element-1151">
              <span className="text-sm font-medium" data-id="element-1152">Success Stories</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1153">
              <span className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] bg-clip-text text-transparent" data-id="element-1154">
                Real patients, real results
              </span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1155">
              Hear from JoinOmu patients who have transformed their lives
              through our weight loss program.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1156">
            {/* Testimonial 1 */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700/30 relative" data-id="element-1157">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#BBDDFF]/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-xl" data-id="element-1158"></div>
              <div className="flex items-center mb-6" data-id="element-1159">
                <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Patient" className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-[#BBDDFF]" data-id="element-1160" />
                <div data-id="element-1161">
                  <h4 className="font-semibold" data-id="element-1162">Sarah K.</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1163">
                    Lost 45 pounds in 6 months
                  </p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-1164">
                "JoinOmu changed my life. The combination of medication and
                continuous support from my provider made all the difference. For
                the first time, I feel in control of my eating habits."
              </p>
              <div className="flex items-center" data-id="element-1165">
                <div className="flex" data-id="element-1166">
                  {[...Array(5)].map((_, i) => <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" data-id="element-1167">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-1168"></path>
                    </svg>)}
                </div>
              </div>
            </div>
            {/* Testimonial 2 */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700/30 relative" data-id="element-1169">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#BBDDFF]/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-xl" data-id="element-1170"></div>
              <div className="flex items-center mb-6" data-id="element-1171">
                <img src="https://images.unsplash.com/photo-1552058544-f2b08422138a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=699&q=80" alt="Patient" className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-[#BBDDFF]" data-id="element-1172" />
                <div data-id="element-1173">
                  <h4 className="font-semibold" data-id="element-1174">James T.</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1175">
                    Lost 60 pounds in 8 months
                  </p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-1176">
                "After years of yo-yo dieting, JoinOmu's medical approach was
                exactly what I needed. The GLP-1 medication reduced my cravings,
                and my provider helped me develop healthier habits."
              </p>
              <div className="flex items-center" data-id="element-1177">
                <div className="flex" data-id="element-1178">
                  {[...Array(5)].map((_, i) => <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" data-id="element-1179">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-1180"></path>
                    </svg>)}
                </div>
              </div>
            </div>
            {/* Testimonial 3 */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700/30 relative" data-id="element-1181">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#BBDDFF]/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-xl" data-id="element-1182"></div>
              <div className="flex items-center mb-6" data-id="element-1183">
                <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=688&q=80" alt="Patient" className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-[#BBDDFF]" data-id="element-1184" />
                <div data-id="element-1185">
                  <h4 className="font-semibold" data-id="element-1186">Maria L.</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1187">
                    Lost 35 pounds in 5 months
                  </p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-1188">
                "The convenience of JoinOmu made all the difference for me. I
                could message my doctor anytime, and medication was delivered
                right to my door. This program fits perfectly into my busy
                life."
              </p>
              <div className="flex items-center" data-id="element-1189">
                <div className="flex" data-id="element-1190">
                  {[...Array(5)].map((_, i) => <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" data-id="element-1191">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-1192"></path>
                    </svg>)}
                </div>
              </div>
            </div>
          </div>
          <div className="text-center mt-12" data-id="element-1193">
            <button className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 font-medium py-3 px-8 rounded-full hover:shadow-lg transition-all" data-id="element-1194">
              View More Success Stories
            </button>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" data-id="element-1195">
        <div className="absolute inset-0 bg-gradient-to-r from-[#BBDDFF]/30 to-[#88AAFF]/30 dark:from-[#BBDDFF]/10 dark:to-[#88AAFF]/10" data-id="element-1196"></div>
        <div className="absolute -top-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-[#BBDDFF]/15 blur-3xl" data-id="element-1197"></div>
        <div className="absolute -bottom-[20%] -right-[10%] w-[60%] h-[60%] rounded-full bg-[#88AAFF]/15 blur-3xl" data-id="element-1198"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-1199">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1200">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1201">
              <span className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] bg-clip-text text-transparent" data-id="element-1202">
                Ready to start your weight loss journey?
              </span>
            </h2>

            <a href="https://app.joinomu.com/patient/onboarding" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#BBDDFF] to-[#88AAFF] text-white font-medium py-3 px-10 rounded-full hover:shadow-lg transition-all text-lg" data-id="element-1203">
              Treatments & Pricing
            </a>
          </div>
        </div>
      </section>
    </div>;
}